import { ReactComponent as DragIcon } from 'assets/icons/drag-indicator.svg';
import Task from 'atoms/Task';
import AddTask from 'containers/Plan/AddTask';
import { ProjectState } from 'core/model';
import Button from 'layout/atoms/Button';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'store';
import { LayoutState, openModal } from 'store/layoutSlice';
import { addTask } from 'store/tasksThunks';
import {
    selectArchivedProject,
    selectProject,
    selectWorkspaceData,
} from 'store/workspaceSlice';
import Header from './Header';
import styles from './Project.module.scss';

const Project = (props: {
    id: string;
    folded?: boolean;
    modal?: boolean;
    onProjectDelete?: any;
    onProjectArchive?: any;
    onProjectUnarchive?: any;
    dragHandleProps?: any;
    archived?: boolean;
    addTask?: boolean;
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const layoutState: LayoutState = useSelector((state: any) => state.layout);
    const [hidden, setHidden] = useState(false);
    const [isAddingTask, setIsAddingTask] = useState(false);

    const projectData: ProjectState = useSelector((state: any) =>
        selectProject(state, props.id)
    );
    const projectArchivedData: any = useSelector((state: any) =>
        selectArchivedProject(state, props.id)
    );
    const workspaceData = useSelector(selectWorkspaceData);
    const hasMoreThan10Tasks =
        projectData?.tasks.filter((t) => t.completedAt === null).length > 10;

    const completedTasksCount =
        (projectData?.completedTasksCount || 0) +
            projectData?.tasks.filter((t) => t.completedAt).length || 0;

    useEffect(() => {
        setHidden(layoutState.projectsReorder);
    }, [dispatch, layoutState.projectsReorder]);

    useEffect(() => {
        // if (props.archived && props.modal) {
        //     dispatch(fetchArchivedProjectTasks(props.id));
        // }
    }, [dispatch, props.id, props.archived, props.modal]);

    if (props.archived) {
        // Don't render if archived project data is not available
        if (!projectArchivedData) {
            return null;
        }

        return (
            <div
                className={`${styles.wrap} ${
                    (props.folded || hidden) && styles.hidden
                }`}
            >
                <div
                    className={styles.indicator}
                    style={{
                        backgroundColor: projectArchivedData?.color || '',
                    }}
                    {...props.dragHandleProps}
                >
                    <DragIcon />
                </div>
                <Header
                    workspaceId={
                        projectArchivedData?.workspaceId || workspaceData.id
                    }
                    project={projectArchivedData}
                    onProjectDelete={props.onProjectDelete}
                    onProjectArchive={props.onProjectArchive}
                    onProjectUnarchive={props.onProjectUnarchive}
                    archived
                    modal={props.modal}
                />
                <div className={styles.main}>
                    {props.modal &&
                        projectArchivedData?.tasks &&
                        projectArchivedData?.tasks?.map((task: any) => (
                            <Task
                                projectRef={projectArchivedData?.projectRef}
                                key={task.id}
                                task={task}
                                deletable={false}
                            />
                        ))}
                    {props.modal &&
                        projectArchivedData?.tasks &&
                        projectArchivedData?.tasks.length === 0 && (
                            <p style={{ textAlign: 'center' }}>
                                Projekt nie zawiera żadnych tasków
                            </p>
                        )}
                </div>
            </div>
        );
    }

    return (
        <div
            className={`${styles.wrap} ${
                (props.folded || hidden) && styles.hidden
            }`}
        >
            <div
                className={styles.indicator}
                style={{ backgroundColor: projectData?.color || '' }}
                {...props.dragHandleProps}
            >
                <DragIcon />
            </div>
            <Header
                workspaceId={projectData.workspaceId}
                project={projectData}
                onProjectDelete={props.onProjectDelete}
                onProjectArchive={props.onProjectArchive}
            />
            {projectData.shared && (
                <small style={{ opacity: 0.8 }}>
                    Shared from: {projectData?.shared.ownerId}
                </small>
            )}
            {
                <div className={styles.main}>
                    {projectData.tasks
                        .filter((t) => t.completedAt === null)
                        .map((task, index) => {
                            if (!props.modal && index < 10) {
                                return (
                                    <Task
                                        projectRef={projectData?.projectRef}
                                        key={task.id}
                                        task={task}
                                    />
                                );
                            }

                            if (props.modal) {
                                return (
                                    <Task
                                        projectRef={projectData?.projectRef}
                                        key={task.id}
                                        task={task}
                                    />
                                );
                            }

                            return null;
                        })}
                    {hasMoreThan10Tasks && !props.modal && (
                        <div className={styles.showAll}>
                            <Button
                                theme="link"
                                onClick={() =>
                                    dispatch(
                                        openModal({
                                            name: 'projectDetails',
                                            props: { id: projectData.id },
                                        })
                                    )
                                }
                            >
                                +
                                {projectData.tasks.filter(
                                    (t) => t.completedAt === null
                                ).length - 10}{' '}
                                more tasks
                            </Button>
                        </div>
                    )}
                </div>
            }
            <div className={styles.footer}>
                {hasMoreThan10Tasks && !props.modal ? (
                    <Button
                        style={{
                            color: projectData?.color || '',
                            fontWeight: '700',
                        }}
                        onClick={() =>
                            dispatch(
                                openModal({
                                    name: 'projectDetails',
                                    props: {
                                        id: projectData.id,
                                        addTask: true,
                                    },
                                })
                            )
                        }
                    >
                        + NEW TASK
                    </Button>
                ) : (
                    <AddTask
                        addTask={props?.addTask || false}
                        color={projectData?.color}
                        placeholder={'Add task...'}
                        onTasksAdded={async (tasks) => {
                            for (let i = 0; i < tasks.length; i++) {
                                const t = tasks[i];
                                await dispatch(
                                    addTask({
                                        workspaceId: projectData.workspaceId,
                                        projectId: projectData.id,
                                        name: t,
                                        planned: false,
                                    })
                                );
                            }
                        }}
                        onAddingStateChange={(isAdding: boolean) =>
                            setIsAddingTask(isAdding)
                        }
                    />
                )}

                {completedTasksCount > 0 && !isAddingTask && (
                    <small style={{ opacity: 0.8 }}>
                        You have completed {completedTasksCount} task
                        {completedTasksCount === 1 ? '' : 's'}
                    </small>
                )}
            </div>
        </div>
    );
};

export default Project;
